'use client';

import React, { forwardRef } from 'react';
import { MenubarProps as _MenubarProps } from '@radix-ui/react-menubar';
import * as MenubarPrimitive from '@radix-ui/react-menubar';

import { CheckMini, ChevronRightMini, EllipseMiniSolid } from '@flowind/icons';
import { clx, DefaultProps, Selectors, useComponentDefaultProps } from '@/styles';
import { createPolymorphicComponent } from '@/utils/create-polymorphic-component';
import useStyles from './menu-bar.styles';

export type MenubarStylesNames = Selectors<typeof useStyles>;

export interface MenubarProps extends DefaultProps<MenubarStylesNames>, _MenubarProps {}

const defaultProps: Partial<MenubarProps> = {};

const MenubarMenu = MenubarPrimitive.Menu;

const MenubarGroup = MenubarPrimitive.Group;

const MenubarPortal = MenubarPrimitive.Portal;

const MenubarSub = MenubarPrimitive.Sub;

const MenubarRadioGroup = MenubarPrimitive.RadioGroup;

const _Menubar = forwardRef<HTMLDivElement, MenubarProps>((props, ref) => {
  const { className, classNames, children, ...others } = useComponentDefaultProps(
    'Menubar',
    defaultProps,
    props,
  );
  const { classes, cx } = useStyles({}, { classNames, name: 'Menubar' });

  return (
    <MenubarPrimitive.Root ref={ref} className={cx(classes.root, className)} {...others}>
      {children}
    </MenubarPrimitive.Root>
  );
}) as any;

_Menubar.displayName = MenubarPrimitive.Root.displayName;

const MenubarTrigger = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <MenubarPrimitive.Trigger
    ref={ref}
    className={clx(
      `flex cursor-default select-none items-center rounded-sm px-3 py-1 text-fg-base text-sm font-medium outline-none 
      focus:bg-bg-base-pressed data-[state=open]:bg-bg-base-pressed`,
      className,
    )}
    {...props}
  />
));
MenubarTrigger.displayName = MenubarPrimitive.Trigger.displayName;

const MenubarSubTrigger = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <MenubarPrimitive.SubTrigger
    ref={ref}
    className={clx(
      `flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-fg-base text-sm outline-none 
      focus:bg-bg-base-pressed data-[state=open]:bg-bg-base-pressed`,
      inset && 'pl-8',
      className,
    )}
    {...props}
  >
    {children}
    <ChevronRightMini className="ml-auto h-4 w-4" />
  </MenubarPrimitive.SubTrigger>
));
MenubarSubTrigger.displayName = MenubarPrimitive.SubTrigger.displayName;

const MenubarSubContent = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <MenubarPrimitive.SubContent
    ref={ref}
    className={clx(
      `z-50 min-w-[8rem] overflow-hidden rounded-md border bg-bg-base p-1 text-fg-base shadow-lg 
      data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 
      data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 
      data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
      className,
    )}
    {...props}
  />
));
MenubarSubContent.displayName = MenubarPrimitive.SubContent.displayName;

const MenubarContent = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Content>
>(({ className, align = 'start', alignOffset = -4, sideOffset = 8, ...props }, ref) => (
  <MenubarPrimitive.Portal>
    <MenubarPrimitive.Content
      ref={ref}
      align={align}
      alignOffset={alignOffset}
      sideOffset={sideOffset}
      className={clx(
        `z-50 min-w-[12rem] overflow-hidden rounded-md border bg-bg-base p-1 text-fg-base shadow-md 
        data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 
        data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 
        data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
        className,
      )}
      {...props}
    />
  </MenubarPrimitive.Portal>
));
MenubarContent.displayName = MenubarPrimitive.Content.displayName;

const MenubarItem = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <MenubarPrimitive.Item
    ref={ref}
    className={clx(
      `relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-fg-base txt-compact-sm 
      outline-none focus:bg-bg-base-pressed data-[disabled]:pointer-events-none data-[disabled]:opacity-50`,
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
));
MenubarItem.displayName = MenubarPrimitive.Item.displayName;

const MenubarCheckboxItem = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <MenubarPrimitive.CheckboxItem
    ref={ref}
    className={clx(
      `relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-fg-base txt-compact-sm outline-none 
      focus:bg-bg-base-pressed focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50`,
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <MenubarPrimitive.ItemIndicator>
        <CheckMini className="h-4 w-4" />
      </MenubarPrimitive.ItemIndicator>
    </span>
    {children}
  </MenubarPrimitive.CheckboxItem>
));
MenubarCheckboxItem.displayName = MenubarPrimitive.CheckboxItem.displayName;

const MenubarRadioItem = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <MenubarPrimitive.RadioItem
    ref={ref}
    className={clx(
      `relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 txt-compact-sm outline-none 
      focus:bg-bg-base-pressed focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50`,
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <MenubarPrimitive.ItemIndicator>
        <EllipseMiniSolid className="h-4 w-4 fill-current" />
      </MenubarPrimitive.ItemIndicator>
    </span>
    {children}
  </MenubarPrimitive.RadioItem>
));
MenubarRadioItem.displayName = MenubarPrimitive.RadioItem.displayName;

const MenubarLabel = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <MenubarPrimitive.Label
    ref={ref}
    className={clx('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)}
    {...props}
  />
));
MenubarLabel.displayName = MenubarPrimitive.Label.displayName;

const MenubarSeparator = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <MenubarPrimitive.Separator
    ref={ref}
    className={clx('-mx-1 my-1 h-px bg-border-base', className)}
    {...props}
  />
));
MenubarSeparator.displayName = MenubarPrimitive.Separator.displayName;

const MenubarShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={clx('ml-auto text-xs tracking-widest text-fg-muted', className)} {...props} />
);
MenubarShortcut.displayname = 'MenubarShortcut';

_Menubar.Menu = MenubarMenu;
_Menubar.Trigger = MenubarTrigger;
_Menubar.SubTrigger = MenubarSubTrigger;
_Menubar.Content = MenubarContent;
_Menubar.SubContent = MenubarSubContent;
_Menubar.Item = MenubarItem;
_Menubar.CheckboxItem = MenubarCheckboxItem;
_Menubar.RadioGroup = MenubarRadioGroup;
_Menubar.RadioItem = MenubarRadioItem;
_Menubar.Label = MenubarLabel;
_Menubar.Separator = MenubarSeparator;
_Menubar.Shortcut = MenubarShortcut;
_Menubar.Portal = MenubarPortal;
_Menubar.Group = MenubarGroup;
_Menubar.Sub = MenubarSub;

export const Menubar = createPolymorphicComponent<
  'Menubar',
  MenubarProps,
  {
    Menu: typeof MenubarMenu;
    Trigger: typeof MenubarTrigger;
    SubTrigger: typeof MenubarSubTrigger;
    Content: typeof MenubarContent;
    SubContent: typeof MenubarSubContent;
    Item: typeof MenubarItem;
    CheckboxItem: typeof MenubarCheckboxItem;
    RadioGroup: typeof MenubarRadioGroup;
    RadioItem: typeof MenubarRadioItem;
    Label: typeof MenubarLabel;
    Separator: typeof MenubarSeparator;
    Shortcut: typeof MenubarShortcut;
    Portal: typeof MenubarPortal;
    Group: typeof MenubarGroup;
    Sub: typeof MenubarSub;
  }
>(_Menubar);
